<template>
  <div class="columns is-multiline">
    <!-- Table search -->
    <div v-if="hasTitle || !hide_table_search" class="column is-12">
      <div class="field is-grouped">
        <p class="control title">
          <title-component class="center-vertically">
            <slot></slot>
          </title-component>
        </p>
        <p v-if="!hide_table_search" class="control search">
          <input v-if="auto_search"
            ref="search" class="input" type="text"
            :placeholder="'Search' | translate"
            v-on:keyup="onSearch" v-model="search">
          <input v-else
            ref="search" class="input" type="text"
            :placeholder="'Search' | translate"
            v-on:keyup.enter="onSearch" v-model="search">
        </p>
      </div>
    </div>

    <!-- Table -->
    <div class="table-holder">
      <b-table
        id="btable"
        class="table is-fullwidth"
        :data="content"
        :loading="loading"
        :paginated="false"
        backend-pagination
        :total="total"
        :per-page="perPage"
        backend-sorting
        :default-sort-direction="'desc'"
        :checked-rows.sync="checkedRows"
        @click="handleRowClick"
        @sort="onSortChange"
        :mobile-cards="true"
        :checkable="checkable">
        <!-- Column headers -->
        <template slot-scope="props" slot="header">
            <!-- column header filters -->
            <div v-if="filters && getUid(props) && getFilter(props)" @click="$event.stopPropagation()">
              <table-filter-component
                :uid="getUid(props)"
                :filter="getFilter(props)"
                :default_toggle="toggleFilters[props.index]"
                :selected="selectedFilters"
                @setDefaultToggle="setDefaultToggle($event, props.index)"
                @setSelectedFilters="setSelectedFilters($event)">
              </table-filter-component>
            </div>
            <!-- column header text -->
            <div>
              {{ props.column.label }}
            </div>
        </template>
        <!-- Column content -->
        <template slot-scope="props">
          <b-table-column
            v-for="(item_key, index) in item_keys"
            :key="index"
            :width="getColumnWidthForKey(item_key)"
            :numeric="isColumnAlignedRight(item_key, index)"
            :field="getSortForKey(item_key)"
            :meta="getFilterForKey(item_key)"
            :label="headings[index] | translate"
            :sortable="Boolean(getSortForKey(item_key))"
            :class="getColumnsToHide(item_key)">
            <table-cell-component
              :cell_item="getItemForKey(props.row, item_key)">
            </table-cell-component>
          </b-table-column>
        </template>

        <template slot="footer">
          <div v-if="isPaginated" class="custom-pagination level">
            <div class="level-left">
              <div class="level-item">
                <span v-translate="{total: total}">All results: %{ total }</span>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-pagination
                  :total="total"
                  :current.sync="currentPage"
                  :per-page="perPage"
                  @change="onPageChange">
                </b-pagination>
              </div>
            </div>
          </div>
        </template>

        <!-- If the table is empty -->
        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p v-translate>The table is empty.</p>
            </div>
          </section>
        </template>
      </b-table>
    </div>

  </div>
</template>
<script>
  import TitleComponent from '@/components/layout/TitleComponent'
  import TableCellComponent from '@/components/layout/table/TableCellComponent'
  import TableFilterComponent from '@/components/layout/table/TableFilterComponent'
  import { getDetailDisplayContext, getPageFromQuery, getSearchFromQuery } from '@/utility/utility_functions'

  const KEY_ENTER = 13

  export default {
    name: 'table-component-buefy',
    data () {
      return {
        loading: false,
        total: this.total_items,
        currentPage: 1,
        currentOrder: null,
        perPage: 0,
        search: null,
        selectedFilters: {},
        toggleFilters: {},
        timer: null,
        checkedRows: []
      }
    },
    props: {
      id: { // only for multiple table support
        default: ''
      },
      headings: {
        type: Array,
        required: true
      },
      filters: {
        type: Object,
        default: () => null
      },
      saved_filters: {
        type: Object,
        default: () => null
      },
      save_filters: {
        type: Boolean,
        default: false
      },
      save_page: {
        type: Boolean,
        default: false
      },
      save_search: {
        type: Boolean,
        default: false
      },
      item_keys: {
        type: Array,
        required: true
      },
      content: {
        type: Array,
        required: true
      },
      display_spec: {
        type: Array
      },
      total_items: {
        type: Number,
        default: 0
      },
      pagination_callback: {
        type: Function,
        required: false
      },
      hidden_columns: {
        type: Array,
        default: () => []
      },
      hide_table_search: {
        type: Boolean,
        default: false
      },
      default_search: {
        type: String,
        default: null
      },
      col_styles: {
        type: Object,
        default: () => ({})
      },
      checkable: {
        type: Boolean,
        default: false
      },
      auto_search: {
        type: Boolean,
        default: false
      }
    },
    components: {
      TitleComponent,
      TableCellComponent,
      TableFilterComponent
    },
    computed: {
      hasTitle () {
        return !!this.$slots.default
      },
      isPaginated () {
        return this.content.length && this.content.length < this.total
      }
    },
    methods: {
      getFilter (props) {
        let filter = this.filters[props.column.meta]
        if (!filter) return
        if (Array.isArray(filter)) {
          filter = { items: filter }
        }
        return filter
      },
      getUid (props) {
        return props.column.meta
      },
      showFilterByIndex (index) {
        return this.showFilters[index]
      },
      setDefaultToggle (toggle, uid) {
        this.toggleFilters[uid] = toggle
      },
      setSelectedFilters (filters) {
        if (this.save_filters) {
          this.$router.push({ query: Object.assign({}, this.$route.query, filters) })
        }
        this.selectedFilters = Object.assign({}, filters)
        this.currentPage = 1
        this.loadPage()
      },
      handleRowClick (row) {
        if (!this.checkedRows.includes(row)) {
          this.checkedRows.push(row)
        } else {
          this.checkedRows = this.checkedRows.filter(i => i !== row)
        }
      },
      onSearch (e) {
        clearTimeout(this.timer)
        if (e.keyCode === KEY_ENTER) {
          this.resetPage()
          if (this.save_search) {
            this.$router.push({ query: Object.assign({}, this.$route.query, { search: this.search }) })
            this.currentPage = 1
            this.setSelectedFilters(this.$route.query)
            return
          }
          this.loadPage()
          return
        }
        this.timer = setTimeout(() => {
          if (this.save_search) {
            this.$router.push({ query: Object.assign({}, this.$route.query, { search: this.search }) })
            this.resetPage()
            this.setSelectedFilters(this.$route.query)
          }
          this.resetPage()
          this.loadPage()
        }, 500)
      },
      loadPage (page, order) {
        if (this.save_page) {
          if (!page) {
            page = getPageFromQuery(this.$route.query)
            this.currentPage = page
          }
        }
        if (this.save_search) {
          if (!this.search) {
            this.search = getSearchFromQuery(this.$route.query)
          }
        }
        let queryParams = {
          page: page || this.currentPage,
          ordering: order || this.currentOrder,
          search: this.search || this.default_search
        }
        Object.assign(queryParams, this.selectedFilters)
        if (this.save_page) {
          this.$router.push({ query: Object.assign({}, this.$route.query, { page: page }) })
        }
        this.loading = true
        this.pagination_callback && this.pagination_callback(queryParams).then((items) => {
          this.loading = false
          this.total = items ? items.count : 0
        })
      },
      resetPage () {
        this.currentPage = 1
        if (this.save_page) {
          this.$router.push({ query: Object.assign({}, this.$route.query, { page: this.currentPage }) })
        }
      },
      onPageChange (page) {
        this.currentPage = page
        this.loadPage(page)
      },
      onSortChange (columnKeys, isAsc) {
        if (isAsc !== 'asc') {
          columnKeys = '-' + columnKeys.replace(',', `,-`)
        }
        this.currentOrder = columnKeys
        this.loadPage(this.currentPage, columnKeys)
      },
      displayContext: function (item) {
        return getDetailDisplayContext(item, this.display_spec)
      },
      getItemForKey (item, key) {
        let displayContext = this.displayContext(item)
        return displayContext.find(i => i.item_key === key)
      },
      getSortForKey (key) {
        return this.display_spec.find(i => i.item_key === key).sort_key
      },
      getFilterForKey (key) {
        return this.display_spec.find(i => i.item_key === key).filter
      },
      getColumnWidthForKey (key) {
        let columnContext = this.display_spec.find(i => i.item_key === key)
        return columnContext.width
      },
      getColumnsToHide (columnKey) {
        return {
          'is-hidden-touch': this.hidden_columns.includes(columnKey)
        }
      },
      getLabelForColumn (column) {
        let colIndex = Array.prototype.indexOf.call(column.parentNode.getElementsByTagName('td'), column)
        return document.getElementById('btable').getElementsByTagName('th')[colIndex]
      },
      hideColumnLabels (colsToHide) {
        for (let col of colsToHide) {
          this.getLabelForColumn(col) && this.getLabelForColumn(col).classList.add('is-hidden-touch')
        }
      },
      getColumnStylesForKey (itemKey) {
        return this.col_styles[itemKey] || []
      },
      isColumnAlignedRight (itemKey, index) {
        let colStyles = this.getColumnStylesForKey(itemKey)
        return index + 1 === this.item_keys.length && !colStyles.includes('alignLeft')
      }
    },
    created () {
      this.perPage = this.content.length
      this.setSelectedFilters(this.saved_filters)
    },
    mounted () {
      this.$refs.search && this.$refs.search.focus()
    },
    updated () {
      this.hideColumnLabels(document.getElementsByClassName('is-hidden-touch'))
    },
    watch: {
      checkedRows () {
        this.$emit(`checked-row-handler${this.id}`, this.checkedRows)
      }
    }
  }
</script>
<style lang="sass" scoped>
  .center-vertically
    line-height: 2.2em
  .is-grouped
    @media(max-width: 768px)
      flex-direction: column
  .title
    flex: 1
  .search
    flex: 1
    margin-bottom: 0.5385em
  .custom-pagination
    margin-top: 3rem
</style>
